'use client';

import { useState } from 'react';
import Link from 'next/link';

type Mode = 'CANCEL' | 'EXCHANGE';

export default function CancelPage() {
  const [mode, setMode]           = useState<Mode>('CANCEL');
  const [orderNo, setOrderNo]     = useState('');
  const [cancelReason, setCancelReason] = useState('고객 요청');
  const [exchangeReason, setExchangeReason] = useState('');
  const [result, setResult]       = useState<string | null>(null);
  const [shippingFee, setShippingFee] = useState<number | null>(null);
  const [loading, setLoading]     = useState(false);

  const switchMode = (m: Mode) => { setMode(m); setResult(null); setShippingFee(null); };

  async function handleCancel() {
    if (!orderNo.trim()) { setResult('주문번호를 입력하세요.'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch('/api/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNo: orderNo.trim(), cancelReason }),
      });
      const data = await res.json() as { ok: boolean; message?: string };
      setResult(data.ok ? '✅ 취소 완료' : `❌ ${data.message ?? '취소 실패'}`);
    } catch {
      setResult('❌ 네트워크 오류');
    } finally {
      setLoading(false);
    }
  }

  async function handleExchange() {
    if (!orderNo.trim()) { setResult('주문번호를 입력하세요.'); return; }
    if (!exchangeReason.trim()) { setResult('교환 사유를 입력하세요.'); return; }
    setLoading(true);
    setResult(null);
    setShippingFee(null);
    try {
      const res = await fetch('/api/exchange-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNo: orderNo.trim(), reason: exchangeReason.trim() }),
      });
      const data = await res.json() as { ok: boolean; message?: string; shippingFee?: number };
      setResult(data.ok ? '✅ 교환 신청이 접수되었습니다. 담당자 확인 후 처리됩니다.' : `❌ ${data.message ?? '신청 실패'}`);
      if (data.ok) setShippingFee(data.shippingFee ?? 0);
    } catch {
      setResult('❌ 네트워크 오류');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 p-5 pb-20">
      <div className="max-w-md mx-auto bg-white rounded-xl p-6 shadow-md">
        <h1 className="text-lg font-bold mb-4 text-black">결제취소 / 교환</h1>

        <div className="flex gap-2 mb-4">
          <button type="button" onClick={() => switchMode('CANCEL')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold ${mode === 'CANCEL' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
            결제취소
          </button>
          <button type="button" onClick={() => switchMode('EXCHANGE')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold ${mode === 'EXCHANGE' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
            교환신청
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-sm text-gray-500 font-medium">주문번호</label>
            <input className="w-full border border-gray-200 rounded-lg p-2 mt-1 text-sm placeholder:text-gray-600 text-gray-900" value={orderNo}
              onChange={(e) => setOrderNo(e.target.value)} placeholder="예: 260721-110118-930283" />
          </div>

          {mode === 'CANCEL' ? (
            <>
              <div>
                <label className="text-sm text-gray-500 font-medium">취소 사유</label>
                <input className="w-full border border-gray-200 rounded-lg p-2 mt-1 text-sm placeholder:text-gray-600 text-gray-900" value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)} />
              </div>
              <button onClick={handleCancel} disabled={loading}
                className="w-full bg-red-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50">
                {loading ? '처리 중...' : '결제 취소 요청'}
              </button>
            </>
          ) : (
            <>
              <div>
                <label className="text-sm text-gray-500 font-medium">교환 사유</label>
                <input className="w-full border border-gray-200 rounded-lg p-2 mt-1 text-sm placeholder:text-gray-600 text-gray-900" value={exchangeReason}
                  onChange={(e) => setExchangeReason(e.target.value)} placeholder="예: 색상 변경 요청" />
              </div>
              <p className="text-xs text-gray-400">교환비용(차액)은 담당자 확인 후 별도 안내되며, 교환배송비는 신청 접수 후 안내됩니다.</p>
              <button onClick={handleExchange} disabled={loading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? '처리 중...' : '교환 신청'}
              </button>
            </>
          )}

          {result && <p className="text-sm text-center font-medium mt-2">{result}</p>}
          {shippingFee !== null && (
            <p className="text-sm text-center text-gray-600 mt-1">
              교환배송비: <span className="font-semibold text-black">{shippingFee.toLocaleString()}원</span>
            </p>
          )}
        </div>
        <div className="mt-6 pt-4 border-t">
          <Link href="/" className="text-blue-600 text-sm">← 처음으로</Link>
        </div>
      </div>
    </div>
  );
}
